<template>
  <div
    class="kpi-card"
    :class="[`kpi-${size}`, { muted, 'is-loading': loading, 'is-empty': empty, 'is-nozones': noZones, 'is-tripwire': tripwire }]"
    :style="{ '--accent': accent }"
  >
    <div class="kpi-head">
      <i v-if="icon" :class="['bi', icon, 'kpi-icon']"></i>
      <span class="kpi-label">{{ label }}</span>
      <Tooltip v-if="tripwire" :text="$t('controls.tripwire.derived')">
        <span class="tw-badge">
          <i class="bi bi-arrow-left-right"></i>{{ $t('controls.tripwire.badge') }}
        </span>
      </Tooltip>
      <slot name="badge" />
    </div>

    <!-- STATE LAYER (same for every card) -->
    <div v-if="loading" class="kpi-state">
      <div class="loader"></div>
      <span class="state-text">{{ loadingText ?? $t('controls.state.loading') }}</span>
    </div>
    <div v-else-if="noZones" class="kpi-state">
      <i class="bi bi-exclamation-triangle state-ico"></i>
      <span class="state-text">{{ noZonesText ?? $t('controls.state.noZones') }}</span>
    </div>
    <div v-else-if="empty" class="kpi-state">
      <i class="bi bi-dash-circle state-ico"></i>
      <span class="state-text">{{ emptyText ?? $t('controls.state.noData') }}</span>
    </div>

    <!-- NORMAL CONTENT -->
    <template v-else>
      <div class="kpi-value">
        <span class="num">
          <slot name="value">{{ value }}</slot>
        </span>
        <span v-if="suffix" class="suffix">{{ suffix }}</span>
      </div>

      <div v-if="$slots.meta" class="kpi-meta">
        <slot name="meta" />
      </div>
    </template>
  </div>
</template>

<script setup>
import Tooltip from '@/components/ui/Tooltip.vue'

defineProps({
  /* visuals */
  label:   { type: String, required: true },
  value:   { type: [String, Number], default: '' },
  suffix:  { type: String, default: '' },
  icon:    { type: String, default: '' },             // e.g. "bi-people"
  accent:  { type: String, default: '#36910d' },
  size:    { type: String, default: 'sm' },           // sm | md | lg

  /* states */
  loading: { type: Boolean, default: false },
  noZones: { type: Boolean, default: false },
  empty:   { type: Boolean, default: false },
  muted:   { type: Boolean, default: false },
  tripwire:{ type: Boolean, default: false },

  /* state messages (override if needed) */
  /* (null = the default text, in the portal language) */
  loadingText: { type: String, default: null },
  noZonesText: { type: String, default: null },
  emptyText:   { type: String, default: null }
})
</script>

<style scoped>
.kpi-card{
  --bg:#fff; --fg:#425461; --sub:#6b7a86;
  background:var(--bg); color:var(--fg);
  border:1px solid #e6edf3; border-left:4px solid var(--accent);
  border-radius:12px; padding:12px 14px; box-shadow:0 3px 12px rgba(13,38,76,.06);
  min-height: 88px;
  display:flex; flex-direction:column; justify-content:center;
}
.kpi-card.muted{ opacity:.65; filter:grayscale(.1) }

/* tripwire variant — semantic "wire" treatment */
.kpi-card.is-tripwire{
  border-left-style: dashed;
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--accent) 6%, #fff) 0, #fff 60%),
    #fff;
}
.tw-badge{
  display:inline-flex; align-items:center; gap:4px;
  margin-left:auto;
  padding:2px 8px;
  font-size:.65rem; font-weight:700; letter-spacing:.3px;
  text-transform:uppercase;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, #fff);
  border:1px dashed color-mix(in srgb, var(--accent) 55%, #fff);
  border-radius:999px;
  line-height:1;
}
.tw-badge i{ font-size:.72rem }

/* flex-wrap + min-width:0 so the label + tripwire badge can never overflow a
   narrow card (badge drops to its own line instead of spilling past the edge). */
.kpi-head{ display:flex; align-items:center; flex-wrap:wrap; gap:.35rem .5rem; margin-bottom:.35rem; min-width:0 }
.kpi-icon{ font-size:1rem; opacity:.9; color:var(--accent); flex:0 0 auto }
.kpi-label{ font-weight:600; letter-spacing:.2px; min-width:0 }
.kpi-head .tt-wrap{ flex:0 0 auto }

/* value row (prop or slot) – consistent typography */
.kpi-value{ display:flex; align-items:baseline; gap:.4rem }
.kpi-value .num{ font-weight:700; line-height:1 }
.kpi-value .suffix{ color:var(--sub); font-weight:600 }

/* meta line */
.kpi-meta{ margin-top:.25rem; color:var(--sub); font-size:.85rem }

/* sizes */
.kpi-sm .num{ font-size:1.2rem }
.kpi-md .num{ font-size:2rem   }
.kpi-lg .num{ font-size:2.4rem }

/* unified state view */
.kpi-state{
  display:flex; align-items:center; gap:.5rem; color:#6b7a86; min-height:40px;
}
.state-ico{ font-size:1.1rem; opacity:.8 }
.loader{
  width:18px; height:18px; border-radius:50%;
  background:#B2C149; padding:2px; aspect-ratio:1;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spin 1s linear infinite;
}
@keyframes spin{to{transform:rotate(1turn)}}
</style>